import React, { useState, useEffect } from "react";
import { SolanaCustomLogo } from "./SolanaCustomLogo";
import {
  ChevronRight,
  TrendingUp,
  TrendingDown,
  BarChart3,
  MessageSquareText,
  Clock,
  Target,
  ChevronDown,
  Send,
  RefreshCw,
} from "lucide-react";
import { useLocation } from "wouter";
import { useLicense } from "@/components/LicenseProvider";
import { perplexityService } from "@/services/PerplexityService";
import { AIResponse } from "@/components/AIResponse";
import { SuggestedQuestions } from "@/components/SuggestedQuestions";
import CoinIcon from './ui/CoinIcon';
import { sanitizeTokens } from './ui/chart';

// Using CryptoCompare API for real coin logos
import { getCoinLogo as getCryptoCompareLogo } from "@/utils/getCoinLogo";

interface SolanaMascotProps {
  messages?: string[];
  position?: "bottom-right" | "mid-right" | "top-right";
  ctaText?: string;
  ctaLink?: string;
  onCtaClick?: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
  marketSentiment?: "bullish" | "bearish" | "neutral";
  hideHamburgerButton?: boolean;
}

interface TokenPrice {
  symbol: string;
  name: string;
  price: number;
  change: number;
  volume?: number;
  trending?: boolean;
  lastUpdate?: number;
  imageUrl?: string;  // URL for coin logo from API
}

interface MarketSentiment {
  current: "bullish" | "bearish" | "neutral";
  trend: string;
  strength: number;
  lastUpdate: number;
}

interface MarketData {
  topPlatforms: string[];
  volumeNeeded: string;
  bestStrategy: string;
  successRate: string;
  pricePrediction: string;
}

// Example messages - promotional
const DEFAULT_MESSAGES = [
  "Boost your Solana token's visibility with our Volume Bot!",
  "Get your token trending on DEXScreener today!",
  "Ready to see your token on Pump.Fun trending page?",
  "Increase volume +1324% with our Solana Volume Bot!",
  "Make your token stand out in the Solana ecosystem!",
];

// Market strategy suggestions
const STRATEGY_MESSAGES = [
  "Need ~1000 transactions to trend on SolanaFM! Our bot can help.",
  "Typically takes 120K vol to trend on Jupiter. We can get you there!",
  "DEXScreener trending requires consistent volume. Let us handle that.",
  "Meme coins need visibility. We provide 400% more exposure on average.",
];

// Market sentiment-based messages
const MARKET_MESSAGES = {
  bullish: [
    "Market looking bullish! Perfect time to increase token visibility.",
    "Bullish sentiment detected! Boost your token's volume now.",
    "SOL is pumping! Get your token trending on DEX platforms.",
    "Bulls are taking control! Maximize your token's exposure today.",
    "26 trending meme coins are up 40%+ in bullish markets, visibility is key!",
    "Tokens with consistent volume are up 84% more in bullish trends.",
  ],
  bearish: [
    "Market is bearish. Stay visible with strategic volume.",
    "During bear markets, visibility becomes even more crucial.",
    "Stand out during downtrends with consistent volume.",
    "Beat the bearish sentiment with optimized token visibility.",
    "Only 4% of tokens maintain visibility during bear markets - be one of them!",
    "Tokens with steady volume drop 37% less during market corrections.",
  ],
  neutral: [
    "Market sentiment is neutral. Good time to build visibility.",
    "Market consolidating - perfect time to grow token awareness.",
    "Steady market conditions - ideal for volume optimization.",
    "Market is waiting for direction - position your token now.",
    "93% of breakout tokens had consistent visibility before market shifts.",
    "Neutral markets are perfect for establishing visibility benchmarks.",
  ],
};

// Market insights based on sentiment
const MARKET_INSIGHTS = {
  bullish: {
    topPlatforms: ["Pump.Fun", "Jupiter", "DEXScreener"],
    volumeNeeded: "120K-180K USD/24h",
    bestStrategy: "Consistent uptrend volume patterns with breaks",
    successRate: "76% trending success rate in bull markets",
    pricePrediction: "SOL likely to test $160-175 range",
  },
  bearish: {
    topPlatforms: ["Birdeye", "DEXScreener", "Tensor"],
    volumeNeeded: "60K-90K USD/24h",
    bestStrategy: "Steady volume with periodic spikes",
    successRate: "38% trending success rate in bear markets",
    pricePrediction: "SOL may retest $130-140 support levels",
  },
  neutral: {
    topPlatforms: ["DEXScreener", "Solscan", "Jupiter"],
    volumeNeeded: "90K-120K USD/24h",
    bestStrategy: "Balanced buy/sell distribution with natural patterns",
    successRate: "54% trending success rate in neutral markets",
    pricePrediction: "SOL likely to consolidate in $145-155 range",
  },
};

// Mock function to use for demo instead of external API
const simulateAIResponse = async () => {
  // Wait 1-2 seconds to simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
  const mockResponses = [
    "For new tokens, effective volume distribution typically requires 75K-140K USD daily volume spread across multiple DEXes to achieve sustainable visibility. Most successful strategies employ a gradual ramp-up approach with 10-15 coordinated wallets distributing trades during peak hours (13:00-15:00 UTC and 01:00-03:00 UTC). This approach maximizes DEX algorithm recognition while maintaining natural trading patterns.",
    "DEX ranking algorithms primarily evaluate volume consistency, transaction diversity, and trader count. Jupiter and Raydium emphasize 24-hour volume sustainability with natural variation (±15-20%) between time periods. To optimize for these factors, recommended distribution includes 55-65% volume during peak hours and remaining volume during off-peak periods, with transaction sizes varying between 0.2-3 SOL equivalent.",
    "For optimal token visibility enhancement, wallet distribution patterns should follow Pareto-like distribution with ~20% of wallets accounting for ~60% of transaction volume. Maintaining 11-15 active wallets with varied transaction timing and sizes creates patterns that DEX ranking algorithms recognize as organic. Avoid uniform transaction sizes and perfectly timed intervals, as these trigger pattern recognition filters.",
    "Volume requirements vary by DEX and market conditions, but current data indicates Jupiter's trending algorithm requires approximately 150K-200K USD daily volume with at least 8-10 hour sustained activity. Recent updates to their ranking system also factor in LP depth stability, making consolidated liquidity across fewer pools (1-2 primary pools) more effective than dispersed liquidity across many small pools.",
    "Based on current market data, optimal volume distribution for new tokens follows a 40/30/20/10 pattern: 40% volume on primary DEX (typically Jupiter/Raydium), 30% on secondary DEX, 20% on tertiary platforms, and 10% reserved for smaller specialty DEXes. This diversity maximizes visibility while building sustainable trading patterns across the ecosystem."
  ];
  return {
    content: mockResponses[Math.floor(Math.random() * mockResponses.length)]
  };
};

export function SimpleSolanaMascot({
  messages = DEFAULT_MESSAGES,
  position = "bottom-right",
  ctaText = "Start Free Trial",
  ctaLink = "#pricing",
  onCtaClick,
  autoHide = false,
  autoHideDelay = 60000, // 1 minute
  marketSentiment = "neutral",
  hideHamburgerButton = false,
}: SolanaMascotProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isHidden, setIsHidden] = useState(false); // New state for scroll-based hiding
  const [isHovered, setIsHovered] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  // Closed on mobile devices, open on desktop
  const [isSpeechBubbleVisible, setIsSpeechBubbleVisible] = useState(window.innerWidth >= 640); // Open above 640px (sm), closed below
  const [showSolanaPrice, setShowSolanaPrice] = useState(true); // SOL price always visible
  const [showStats, setShowStats] = useState(false); // Stats initially closed
  const [showAssistant, setShowAssistant] = useState(false); // AI Assistant initially closed
  const [showMarketData, setShowMarketData] = useState(false); // Market data initially closed
  const [userQuestion, setUserQuestion] = useState("");
  const [aiResponses, setAiResponses] = useState<string[]>([]);
  const [solanaPriceData, setSolanaPriceData] = useState<TokenPrice>({
    symbol: "SOL",
    name: "Solana",
    price: 145.82,
    change: 2.4,
    lastUpdate: Date.now(),
  });
  const [location, setLocation] = useLocation();
  const { openRegistrationModal } = useLicense();

  // Select messages based on market sentiment
  const sentimentMessages = MARKET_MESSAGES[marketSentiment];
  const strategyMessages = STRATEGY_MESSAGES;
  const combinedMessages = [
    ...messages,
    ...sentimentMessages,
    ...strategyMessages,
  ];

  // CSS classes for positioning - Fixed positions for each screen size
  const positionClasses = {
    "bottom-right": "bottom-24 sm:right-7 right-7",
    "mid-right": "bottom-1/3 sm:right-7 right-7",
    "top-right": "top-32 sm:right-7 right-7",
  };

  // State for additional tokens and market data
  const [additionalTokens, setAdditionalTokens] = useState<TokenPrice[]>([
    // Default meme coins data to ensure they're always displayed
    { symbol: 'JUP', name: 'Jupiter', price: 2.43, change: 3.2, volume: 189500000, trending: true },
    { symbol: 'RAY', name: 'Raydium', price: 1.07, change: 1.8, volume: 43200000, trending: false },
    { symbol: 'BONK', name: 'Bonk', price: 0.000018, change: 5.2, volume: 91200000, trending: true },
    { symbol: 'WIF', name: 'Dogwifhat', price: 1.35, change: 2.5, volume: 125600000, trending: false }
  ]);
  const [marketSentimentData, setMarketSentimentData] =
    useState<MarketSentiment>({
      current: "neutral",
      trend: "stable",
      strength: 0.5,
      lastUpdate: Date.now(),
    });
  const [marketInsight, setMarketInsight] = useState<string>("");
  const [volumeMarketData, setVolumeMarketData] = useState<MarketData>({
    topPlatforms: [],
    volumeNeeded: "",
    bestStrategy: "",
    successRate: "",
    pricePrediction: "",
  });

  // Perplexity AI related states
  const [aiResponse, setAiResponse] = useState<string>("");
  const [aiResponseLoading, setAiResponseLoading] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string>("");
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(
    perplexityService.getSuggestedQuestions(),
  );

  // Rotating messages - showing with ideal timing for users to read
  useEffect(() => {
    const messageInterval = setInterval(() => {
      if (combinedMessages.length > 1) {
        setCurrentMessageIndex((prev) => (prev + 1) % combinedMessages.length);
      }
    }, 4500); // Changes every 4.5 seconds - enough time to read without waiting too long

    return () => clearInterval(messageInterval);
  }, [combinedMessages]);

  // Auto-hide after delay if enabled
  useEffect(() => {
    if (autoHide) {
      const hideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, autoHideDelay);

      return () => clearTimeout(hideTimeout);
    }
  }, [autoHide, autoHideDelay]);

  // Add scroll listener to hide mascot when scrolling down
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // If we've scrolled down more than 200px, hide the mascot
          if (window.scrollY > 200 && window.scrollY > lastScrollY) {
            setIsHidden(true);
          } else if (window.scrollY < lastScrollY) {
            // When scrolling back up, show mascot again
            setIsHidden(false);
          }

          lastScrollY = window.scrollY;
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle CTA button click
  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
      return;
    }

    openRegistrationModal();

    if (ctaLink.startsWith("#")) {
      const element = document.querySelector(ctaLink);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // Manage mobile/desktop behavior when screen size changes
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  useEffect(() => {
    const handleResize = () => {
      // Detect screen size change
      const mobileCheck = window.innerWidth < 640;
      setIsMobile(mobileCheck);
      
      // Automatically close when switching to mobile device
      if (mobileCheck) {
        setIsSpeechBubbleVisible(false);
      }
      
      // Force refresh the page so that button position is calculated correctly
      // Note: Normally this approach is not ideal but in this case it's the most practical solution
      if (mobileCheck !== isMobile) {
        setTimeout(() => {
          // Update button styles without refreshing the page
          const toggleButton = document.querySelector("[data-solana-toggle]");
          if (toggleButton) {
            const btn = toggleButton as HTMLElement;
            btn.style.left = mobileCheck ? '50%' : 'auto';
            btn.style.right = !mobileCheck ? '0' : 'auto';
            btn.style.transform = mobileCheck ? 'translateX(-50%)' : 'none';
          }
        }, 10);
      }
    };

    // Check during initial loading and on size changes
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  // Toggle speech bubble
  const toggleSpeechBubble = () => {
    setIsSpeechBubbleVisible((prev) => !prev);
  };
  
  // Handle manual refresh button click (no-op, since WebSocket is removed)
  const handleRefreshData = () => {
    // No operation
  };

  // Handle AI question submission - use Perplexity API
  const handleQuestionSubmit = async () => {
    if (!userQuestion.trim()) return;
    setAiResponseLoading(true);
    setAiError("");
    try {
      setAiResponses((prev) => [...prev, `You: ${userQuestion}`]);
      // Use local simulation for demo/testing
      const response = await simulateAIResponse();
      if (response.content) {
        setAiResponse(response.content);
        setAiResponses((prev) => [...prev, response.content]);
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
      setAiError(
        "Our AI assistant is currently overloaded. Please try again later.",
      );
    } finally {
      setAiResponseLoading(false);
      setUserQuestion("");
    }
  };

  // Handle suggested question click
  const handleSuggestedQuestionClick = (question: string) => {
    setUserQuestion(question);
    // Auto-submit the question
    setTimeout(() => {
      handleQuestionSubmit();
    }, 50);
  };

  if (!isVisible) return null;

  // Only render the assistant button (no speech bubble/sentiment area)
  if (isHidden) {
    return (
      <div className="fixed bottom-20 right-7 z-50">
        <button
          onClick={() => {
            const hero = document.getElementById('hero');
            if (hero) hero.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setIsHidden(false);
          }}
          className="bg-gradient-to-r from-[#14F195] to-[#9945FF] rounded-full p-3 shadow-lg shadow-[#14F195]/20 hover:shadow-[#14F195]/40 transition-all duration-300"
          aria-label="Show mascot"
          title="Show market assistant"
          data-solana-toggle
        >
          <div className="h-5 w-5 rounded-full">
            <CoinIcon symbol={typeof solanaPriceData.symbol === 'string' && solanaPriceData.symbol.trim() ? solanaPriceData.symbol : 'UNKNOWN'} size={20} preferredFormat="svg" />
          </div>
        </button>
      </div>
    );
  }

  // Only render the container for the assistant button (no sentiment/speech bubble)
  return (
    <div
      className={`fixed ${positionClasses[position]} z-50 flex flex-col items-center`}
      style={{ width: '320px', left: '50%', transform: 'translateX(-50%)', bottom: '32px' }}
      onClick={() => {
        const hero = document.getElementById('hero');
        if (hero) hero.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }}
    >
      {/* No speech bubble or sentiment area rendered */}
    </div>
  );
}
